import type { VariableDefinition } from "@babackapp/variables-registry";
import type { JSONContent } from "@tiptap/react";


/**
 * Parses a string containing {{variable}} syntax and returns a TipTap JSON document
 * where each {{variable}} is replaced by a mention node.
 *
 * Example:
 *   Input: "/track{{shop.name}}"
 *   Output: {
 *     type: "doc",
 *     content: [
 *       {
 *         type: "paragraph",
 *         content: [
 *           { type: "text", text: "/track" },
 *           { type: "mention", attrs: { id: "shop.name", label: "Shop Name" } }
 *         ]
 *       }
 *     ]
 *   }
 *
 * When useHardBreaks is true, \n characters are converted to hardBreak nodes
 * instead of being kept as literal characters in text nodes.
 */
export function parseMentionsFromText(
  text: string,
  variables: VariableDefinition[],
  options?: { useHardBreaks?: boolean },
): JSONContent {
  const regex = /\{\{([^}]+)\}\}/g;
  const nodes: JSONContent[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the mention
    if (match.index > lastIndex) {
      const segment = text.slice(lastIndex, match.index);
      pushTextNodes(nodes, segment, options?.useHardBreaks);
    }
    // Add the mention node
    const variableId = match[1];
    const variable =
      variables.find((v) => v.id === variableId) ||
      variables.find((v) => v.name === variableId);
    const id = variable?.id || variableId;
    const label = variable?.name || variableId;

    nodes.push({
      type: "mention",
      attrs: { id, label },
    });
    lastIndex = regex.lastIndex;
  }
  // Add any remaining text
  if (lastIndex < text.length) {
    const segment = text.slice(lastIndex);
    pushTextNodes(nodes, segment, options?.useHardBreaks);
  }
  const paragraph: JSONContent = { type: "paragraph" };

  if (nodes.length > 0) {
    paragraph.content = nodes;
  } else if (text.length > 0) {
    paragraph.content = [{ type: "text", text }];
  }

  return {
    type: "doc",
    content: [paragraph],
  };
}

function pushTextNodes(
  nodes: JSONContent[],
  text: string,
  useHardBreaks?: boolean,
): void {
  if (!useHardBreaks || !text.includes("\n")) {
    nodes.push({ type: "text", text });
    return;
  }
  const parts = text.split("\n");
  for (let i = 0; i < parts.length; i++) {
    if (i > 0) {
      nodes.push({ type: "hardBreak" });
    }
    if (parts[i].length > 0) {
      nodes.push({ type: "text", text: parts[i] });
    }
  }
}
